
import React from 'react';
import CampaignDetails from '../CampaignDetails';
import AudienceSelection from '../audience';
import MessageComposition from '../MessageComposition';
import CampaignReview from '../CampaignReview';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';

interface CampaignContentProps {
  activeStep: string;
  campaignData: any;
  updateCampaignData: (data: any) => void;
  audienceSize: number;
  audienceLoading: boolean;
  audience: any[];
  isSubmitting: boolean;
  userRestaurants: any[];
  onSaveCampaign: () => Promise<boolean>;
}

const CampaignContent: React.FC<CampaignContentProps> = ({
  activeStep,
  campaignData,
  updateCampaignData,
  audienceSize,
  audienceLoading,
  audience,
  isSubmitting,
  userRestaurants,
  onSaveCampaign
}) => {
  const { user } = useAuth();
  const { 
    restaurantSmsStatus, 
    enableRestaurantSms,
    canEnableSms
  } = useSmsStatus();
  
  const [showEnableDialog, setShowEnableDialog] = React.useState(false);
  const [enablingRestaurants, setEnablingRestaurants] = React.useState<string[]>([]);
  const [isEnablingSms, setIsEnablingSms] = React.useState(false);
  const [disabledRestaurants, setDisabledRestaurants] = React.useState<any[]>([]);
  
  React.useEffect(() => {
    // Update disabled restaurants list when selection or status changes
    if (campaignData.selectedRestaurants && restaurantSmsStatus.size > 0) {
      const restaurantsToCheck = campaignData.selectedRestaurants.includes('all')
        ? userRestaurants.map(r => r.id)
        : campaignData.selectedRestaurants;
      
      const disabled = userRestaurants.filter(restaurant => 
        restaurantsToCheck.includes(restaurant.id) && 
        restaurantSmsStatus.has(restaurant.id) && 
        !restaurantSmsStatus.get(restaurant.id)
      );
      
      setDisabledRestaurants(disabled);
    }
  }, [campaignData.selectedRestaurants, restaurantSmsStatus, userRestaurants]);
  
  const handleSaveCampaign = async () => {
    // Check if any selected restaurants have SMS disabled
    if (disabledRestaurants.length > 0 && user) {
      // Check if user can enable SMS for these restaurants
      const canUserEnable = disabledRestaurants.some(restaurant => 
        canEnableSms(user, restaurant.id)
      );
      
      if (canUserEnable) {
        // Show dialog to enable SMS
        setShowEnableDialog(true);
        return false;
      } else {
        // User cannot enable, show error
        toast({
          title: "SMS Not Enabled",
          description: `SMS is not enabled for: ${disabledRestaurants.map(r => r.name).join(', ')}. Contact an administrator.`,
          variant: "destructive"
        });
        return false;
      }
    }
    
    // If all restaurants have SMS enabled, proceed with save
    return await onSaveCampaign();
  };
  
  const handleEnableSmsForAll = async () => {
    setIsEnablingSms(true);
    setEnablingRestaurants(disabledRestaurants.map(r => r.id));
    
    try {
      const results = await Promise.all(
        disabledRestaurants.map(async (restaurant) => {
          if (user && canEnableSms(user, restaurant.id)) {
            return enableRestaurantSms(restaurant.id)
              .then(success => ({ id: restaurant.id, name: restaurant.name, success }))
              .catch(error => ({ id: restaurant.id, name: restaurant.name, success: false, error }));
          }
          return { id: restaurant.id, name: restaurant.name, success: false, error: "No permission" };
        })
      );
      
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        toast({
          title: "SMS Enabled",
          description: `Successfully enabled SMS for ${successful.length} restaurant(s)`,
        });
      }
      
      if (failed.length > 0) {
        toast({
          title: "Some SMS Activations Failed",
          description: `Failed to enable SMS for ${failed.length} restaurant(s)`,
          variant: "destructive"
        });
        return false;
      }
      
      // If all were successful, continue with save
      if (failed.length === 0) {
        setShowEnableDialog(false);
        return await onSaveCampaign();
      }
      
      return false;
    } catch (error) {
      console.error("Error enabling SMS:", error);
      toast({
        title: "SMS Activation Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsEnablingSms(false);
      setEnablingRestaurants([]);
    }
  };
  
  return (
    <div className="space-y-6">
      {activeStep === 'details' && (
        <CampaignDetails 
          campaignData={campaignData} 
          updateCampaignData={updateCampaignData} 
        />
      )}
      
      {activeStep === 'audience' && (
        <AudienceSelection 
          campaignData={campaignData} 
          updateCampaignData={updateCampaignData} 
          audienceSize={audienceSize} 
          audienceLoading={audienceLoading}
          userRestaurants={userRestaurants}
        />
      )}
      
      {activeStep === 'message' && (
        <MessageComposition 
          campaignData={campaignData} 
          updateCampaignData={updateCampaignData} 
          userRestaurants={userRestaurants}
        />
      )}
      
      {activeStep === 'review' && (
        <CampaignReview 
          campaignData={campaignData}
          audienceSize={audienceSize}
          isSubmitting={isSubmitting}
          onSaveCampaign={handleSaveCampaign}
          userRestaurants={userRestaurants}
          disabledRestaurants={disabledRestaurants}
        />
      )}
      
      {/* SMS Enable Dialog */}
      <AlertDialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable SMS for Selected Restaurants?</AlertDialogTitle>
            <AlertDialogDescription>
              SMS is not currently enabled for {disabledRestaurants.length} restaurant(s). Would you like to enable SMS for these restaurants before sending your campaign?
              
              <div className="mt-4 p-3 bg-muted rounded-md">
                <ul className="list-disc pl-5 space-y-1">
                  {disabledRestaurants.map(restaurant => (
                    <li key={restaurant.id} className="text-sm">
                      {restaurant.name}
                      {enablingRestaurants.includes(restaurant.id) && (
                        <span className="ml-2 text-muted-foreground italic flex items-center">
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Enabling...
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnablingSms}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleEnableSmsForAll();
              }}
              disabled={isEnablingSms}
            >
              {isEnablingSms ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enabling SMS...
                </>
              ) : (
                <>Enable SMS & Continue</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignContent;
